/**
 * Device-aware "is this number good or bad" judgment for the performance metrics.
 * Pure — no DOM. The browser bits (reading navigator / performance.memory into a
 * {@link DeviceProfile}) live in the service; here we only turn numbers + profile
 * into a rating so it can be unit-tested and reused (UI colour + AI report).
 *
 * The key idea: don't hardcode "60 fps good, 30 bad". The display refresh caps
 * fps, so a 60 Hz phone can never exceed 60 and a 120 Hz desktop targets 120 —
 * derive the target from the OBSERVED PEAK instead, and rate relative to it.
 */

export type MetricRating = "good" | "warn" | "bad";

/** Device signals we can read in-browser — all optional ("whatever we can get"). */
export interface DeviceProfile {
    /** navigator.deviceMemory, GB (coarse: 0.25/0.5/1/2/4/8; Chrome only) */
    deviceMemoryGB?: number;
    /** navigator.hardwareConcurrency (logical cores) */
    cores?: number;
    /** userAgentData.mobile, or a coarse-pointer fallback */
    mobile?: boolean;
    /** performance.memory.jsHeapSizeLimit, bytes (the heap ceiling; Chrome only) */
    heapLimitBytes?: number;
    /**
     * Estimated usable VRAM budget, bytes. Browsers don't expose real VRAM, so the
     * service ESTIMATES this from the device (or a PRO target profile sets it
     * explicitly); {@link rateVram} only rates against whatever budget it's given.
     */
    vramBudgetBytes?: number;
    /**
     * Budget for rasterized triangles PER FRAME (the GPU-work number, all passes) —
     * often the real ceiling on mobile/XR. Only target profiles set it; auto leaves
     * it unset (no device triangle budget to measure against).
     */
    maxTrianglesPerFrame?: number;
}

/** A named target device (PRO): rate/audit against what you SHIP to, not your dev machine. */
export interface DeviceProfilePreset {
    readonly id: string;
    readonly label: string;
    readonly profile: DeviceProfile;
}

const GB = 1024 * 1024 * 1024;

/**
 * Built-in target devices. Budgets are DELIBERATE ESTIMATES (browsers expose no
 * real VRAM), tuned to be a useful "will it run there" signal, not vendor-exact —
 * a custom profile can override any of them later.
 */
export const DEVICE_PRESETS: readonly DeviceProfilePreset[] = [
    { id: "desktop",     label: "Desktop",                  profile: { heapLimitBytes: 4 * GB,                vramBudgetBytes: 4 * GB,                maxTrianglesPerFrame: 5_000_000, mobile: false } },
    { id: "iphone-mid",  label: "Mobile (iPhone, mid)",     profile: { heapLimitBytes: Math.round(1.5 * GB),  vramBudgetBytes: GB,                    maxTrianglesPerFrame:   750_000, mobile: true, deviceMemoryGB: 4 } },
    { id: "android-low", label: "Mobile (low-end Android)", profile: { heapLimitBytes: Math.round(0.75 * GB), vramBudgetBytes: Math.round(0.5 * GB),  maxTrianglesPerFrame:   300_000, mobile: true, deviceMemoryGB: 2 } },
    { id: "quest",       label: "XR (Quest 3)",             profile: { heapLimitBytes: GB,                    vramBudgetBytes: GB,                    maxTrianglesPerFrame:   500_000, mobile: true, deviceMemoryGB: 6 } },
];

export interface PerformanceRatings {
    fps?: MetricRating;
    memory?: MetricRating;
    gpuMemory?: MetricRating;
    triangles?: MetricRating;
}

/** Standard display refresh cadences a peak fps is snapped to. */
const FPS_CADENCES = [30, 60, 72, 90, 120, 144, 165, 240];

/**
 * Snap an observed peak fps to the nearest standard display cadence — the peak
 * approximates the display's refresh rate (which caps fps). No data yet → assume
 * 60. Never below 30. On a tie, the higher cadence wins (a struggling 60 Hz reads
 * as 60-target, not 30).
 */
export function estimateTargetFps(peakFps: number): number {
    if (!Number.isFinite(peakFps) || peakFps <= 0) return 60;
    let best = FPS_CADENCES[0];
    let bestDist = Infinity;
    for (const c of FPS_CADENCES) {
        const d = Math.abs(c - peakFps);
        if (d <= bestDist) { bestDist = d; best = c; } // <= so higher cadence wins ties
    }
    return best;
}

/** Rate fps against its device target: good ≥90%, warn ≥50%, bad below. */
/**
 * How much of a frame's budget ONE render pass eats.
 *
 * Rated against the budget the display gives you (1000/targetFps), not against
 * the frame's own measured total: the main scene pass is always the biggest
 * share of its own frame, which tells you nothing, whereas "this single pass
 * spends half of your 16.7 ms" is exactly the thing to colour. It also scales
 * with the display — 4 ms is a quarter of a 60 Hz frame and half of a 120 Hz one.
 */
export function ratePassGpuMs(ms: number, targetFps: number): MetricRating {
    if (!Number.isFinite(ms) || !Number.isFinite(targetFps) || targetFps <= 0) return "good";
    const share = ms / (1000 / targetFps);
    if (share >= 0.5) return "bad";
    if (share >= 0.25) return "warn";
    return "good";
}

export function rateFps(fps: number, targetFps: number): MetricRating {
    if (!Number.isFinite(fps) || targetFps <= 0) return "good";
    const ratio = fps / targetFps;
    if (ratio >= 0.9) return "good";
    if (ratio >= 0.5) return "warn";
    return "bad";
}

/**
 * Rate fps STABILITY over a window (the per-second history): the fraction of
 * seconds that dipped below target. A currently-fine reading shouldn't read "good"
 * if it spent much of the last minute below target — sustained drops are the
 * complaint even when the instant is smooth. Needs a few samples; else undefined.
 * (Samples are 1s fps averages, so this catches ~second-scale hitches, not a single
 * janky frame.)
 */
export function rateFrameStability(fpsSamples: readonly number[], targetFps: number): MetricRating | undefined {
    if (targetFps <= 0 || fpsSamples.length < 5) return undefined;
    const threshold = targetFps * 0.9; // a "dropped" second = avg fps below 90% of target
    let below = 0;
    let counted = 0;
    for (const f of fpsSamples) {
        if (!Number.isFinite(f)) continue;
        counted++;
        if (f < threshold) below++;
    }
    if (counted < 5) return undefined;
    const fraction = below / counted;
    if (fraction >= 0.5) return "bad";
    if (fraction >= 0.2) return "warn";
    return "good";
}

const RATING_RANK: Record<MetricRating, number> = { good: 0, warn: 1, bad: 2 };
/** The worst (most severe) of the given ratings, ignoring undefined. */
export function worstRating(...ratings: (MetricRating | undefined)[]): MetricRating | undefined {
    let worst: MetricRating | undefined;
    for (const r of ratings) {
        if (r && (worst === undefined || RATING_RANK[r] > RATING_RANK[worst])) worst = r;
    }
    return worst;
}

/**
 * Rate JS heap use against the heap ceiling: good <70%, warn <90%, bad ≥90%
 * (approaching the limit means GC pressure / OOM risk). Returns undefined when no
 * ceiling is known — we don't invent a "bad" without a real limit to measure against.
 */
export function rateMemory(usedBytes: number, profile: DeviceProfile): MetricRating | undefined {
    const limit = profile.heapLimitBytes;
    if (!limit || limit <= 0 || !Number.isFinite(usedBytes)) return undefined;
    const ratio = usedBytes / limit;
    if (ratio >= 0.9) return "bad";
    if (ratio >= 0.7) return "warn";
    return "good";
}

/**
 * Rate GPU/VRAM use against the device's estimated VRAM budget: good <70%,
 * warn <90%, bad ≥90%. Returns undefined when no budget is known (browsers don't
 * report real VRAM — the budget is an estimate, so this is a soft signal).
 */
export function rateVram(usedBytes: number, profile: DeviceProfile): MetricRating | undefined {
    const budget = profile.vramBudgetBytes;
    if (!budget || budget <= 0 || !Number.isFinite(usedBytes)) return undefined;
    const ratio = usedBytes / budget;
    if (ratio >= 0.9) return "bad";
    if (ratio >= 0.7) return "warn";
    return "good";
}

/**
 * Rate rasterized triangles/frame against the target device's budget: good <70%,
 * warn <90%, bad ≥90%. Undefined when the profile sets no triangle budget (auto).
 */
export function rateTriangles(perFrame: number, profile: DeviceProfile): MetricRating | undefined {
    const budget = profile.maxTrianglesPerFrame;
    if (!budget || budget <= 0 || !Number.isFinite(perFrame)) return undefined;
    const ratio = perFrame / budget;
    if (ratio >= 0.9) return "bad";
    if (ratio >= 0.7) return "warn";
    return "good";
}
